import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";
import { TrafficLightReveal } from "@/components/animations/TrafficLightReveal";
import { CountUp, DeltaCountUp } from "@/components/animations/CountUp";
import { ChevronDown, ChevronUp } from "lucide-react";

type TrafficStatus = "green" | "amber" | "red" | "neutral";

interface KPIStatusItem {
  name: string;
  value: number;
  unit: string;
  status: TrafficStatus;
  delta?: number;
  deltaPercent?: number;
}

interface RichMessageProps {
  content: string;
  data?: Record<string, unknown>;
  isStreaming?: boolean;
  animate?: boolean;
}

function parseStatusItems(data: Record<string, unknown>): KPIStatusItem[] | null {
  if (!data) return null;
  
  const statuses = data.kpi_statuses || data.statuses || data.kpis;
  if (Array.isArray(statuses)) {
    return statuses.map((item: any) => ({
      name: item.name || item.kpi_name || "Unknown",
      value: item.value ?? item.latest_value ?? 0,
      unit: item.unit || "#",
      status: (item.status?.replace("🟢", "green").replace("🟡", "amber").replace("🔴", "red").replace("⚪", "neutral") || "neutral") as TrafficStatus,
      delta: item.delta,
      deltaPercent: item.delta_percent,
    }));
  }
  
  return null;
}

function parseFocusAreas(data: Record<string, unknown>): string[] | null {
  const areas = data.focus_areas || data.focusAreas;
  if (Array.isArray(areas)) {
    return areas.map((a: any) => (typeof a === "string" ? a : a.area || a.name || a.kpi || String(a)));
  }
  return null;
}

export function RichMessage({ content, data, isStreaming, animate = true }: RichMessageProps) {
  const [expanded, setExpanded] = useState(false);
  
  const statusItems = useMemo(() => data ? parseStatusItems(data) : null, [data]);
  const focusAreas = useMemo(() => data ? parseFocusAreas(data) : null, [data]);

  return (
    <div className="space-y-3">
      {/* Markdown rendered content */}
      <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-headings:my-2 prose-strong:text-foreground prose-code:text-xs prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>

      {/* Evidence Mechanics: KPI Status Cards */}
      {statusItems && statusItems.length > 0 && (
        <div className="space-y-2 pt-1">
          {statusItems.slice(0, expanded ? undefined : 5).map((item, index) => (
            <div
              key={item.name}
              className={cn(
                "flex items-center gap-3 p-3 rounded-xl bg-background/50 border border-border/50",
                "transition-all duration-300",
                item.status === "red" && "border-danger/30 bg-danger-bg/30",
                animate && "animate-in-up"
              )}
              style={{ animationDelay: animate ? `${index * 120}ms` : undefined }}
            >
              <TrafficLightReveal
                status={item.status}
                delay={animate ? index * 120 : 0}
                showPulse={item.status === "red"}
                size="md"
              />
              
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{item.name}</p>
              </div>

              <div className="flex items-center gap-2 text-right">
                <span className="text-sm font-semibold tabular-nums">
                  {animate ? (
                    <CountUp
                      end={item.value}
                      duration={0.8}
                      decimals={item.unit === "%" ? 1 : 0}
                      prefix={item.unit === "₹" ? "₹" : ""}
                      suffix={item.unit === "%" ? "%" : ""}
                    />
                  ) : (
                    `${item.unit === "₹" ? "₹" : ""}${item.value}${item.unit === "%" ? "%" : ""}`
                  )}
                </span>

                {item.delta !== undefined && item.delta !== 0 && (
                  <span className="text-xs tabular-nums">
                    {animate ? (
                      <DeltaCountUp
                        end={item.deltaPercent ?? item.delta}
                        duration={0.6}
                        decimals={1}
                        suffix="%"
                      />
                    ) : (
                      <span className={cn(
                        item.delta > 0 ? "text-success" : "text-danger"
                      )}>
                        {item.delta > 0 ? "+" : ""}{(item.deltaPercent ?? item.delta).toFixed(1)}%
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          ))}

          {statusItems.length > 5 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-smooth px-3"
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {expanded ? "Show less" : `+${statusItems.length - 5} more`}
            </button>
          )}
        </div>
      )}

      {/* Focus Areas */}
      {focusAreas && focusAreas.length > 0 && (
        <div
          className={cn("p-3 rounded-xl bg-danger-bg/30 border border-danger/20", animate && "animate-in-up")}
          style={{ animationDelay: animate ? `${(statusItems?.length || 0) * 120 + 200}ms` : undefined }}
        >
          <p className="text-xs font-semibold text-danger mb-2">🎯 Focus on:</p>
          <div className="space-y-1">
            {focusAreas.map((area, i) => (
              <p key={i} className="text-xs text-foreground flex items-center gap-2">
                <span className="text-danger">•</span>
                {area}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
